/*
 * (c) Copyright, 2022, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Button, FormGroup, Form } from 'react-bootstrap'
import { Formik, Field } from 'formik'
import { useServiceContext } from '~/root'
import Modal from '~/components/Modal'
import Loader from '~/components/shared/Loader'
import { InfiniteScrollSelect } from '~/components/InfiniteScrollSelect'
import { usersApiService } from '../../../../services/api/users'
import { groupsApiService } from '~/services/api'
import { toast } from '../../../../services/toast'
interface ManageUserGroupsModalProps {
  userId: number
  onClose?: () => void
}

interface GroupOption {
  value: number
  label: string
}

interface User {
  groupIds?: number[]
  currentSystemGroups?: GroupOption[]
  currentUserDefinedGroups?: GroupOption[]
  currentCAGroups?: GroupOption[]
  plain?: () => User
}

const ManageUserGroupsModal: React.FC<ManageUserGroupsModalProps> = ({ userId, onClose }) => {
  const context = useServiceContext()
  const translate = context.service.filter.translate
  const [isOpen, setIsOpen] = useState(false)
  const [user, setUser] = useState<User>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setIsOpen(true)
    ;(async () => {
      const userResponse = await usersApiService.getById(userId)
      // Simulate .plain() if present
      const userData = userResponse
      const systemUserRolesResponse = await groupsApiService.getList({
        id: userData.groupIds,
        pageSize: userData.groupIds?.length || 0
      })

      const elements = systemUserRolesResponse.elements || []
      const currentSystemGroups = elements
        .filter((g: any) => g.type === 'SYSTEM')
        .map((g: any) => ({ value: g.id, label: g.name }))
      const currentUserDefinedGroups = elements
        .filter((g: any) => g.type === 'USER_DEFINED')
        .map((g: any) => ({ value: g.id, label: g.name }))
      const currentCAGroups = elements
        .filter((g: any) => g.type === 'CLOUDADMIN_ACL')
        .map((g: any) => ({ value: g.id, label: g.name }))
      setUser({ ...userData, currentSystemGroups, currentUserDefinedGroups, currentCAGroups })
      setLoading(false)
    })()
  }, [userId])

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  return (
    <Modal show={isOpen} handleClose={doClose} title={translate('USERS_ACTION_ADD_TO_GROUP')}>
      <Formik
        initialValues={{
          createRATasksNow: false,
          assignTo: user.currentUserDefinedGroups || []
        }}
        enableReinitialize
        onSubmit={(values) => {
          setLoading(true)
          const groupIds = [
            ...(values.assignTo?.map((g: GroupOption) => g.value) || []),
            ...(user.currentSystemGroups?.map((g: GroupOption) => g.value) || []),
            ...(user.currentCAGroups?.map((g: GroupOption) => g.value) || [])
          ]
          usersApiService.updateGroups(userId, groupIds, values.createRATasksNow).then(
            () => {
              setLoading(false)
              toast.success('USERS_GROUPS_EDITED_SUCCESS_MSG')
              doClose()
            },
            (data: any) => {
              setLoading(false)
              toast.error(translate('USERS_UPDATE_FAIL_MSG') + ': ' + data.data.message)
            }
          )
        }}
      >
        {({ values, setFieldValue, handleSubmit }) => (
          <Loader blocking={loading}>
            <Modal.Body>
              <FormGroup className="wparag d-flex flex-column gap-4" style={{ alignItems: 'flex-start', gap: '1rem' }}>
                <label>{translate('SYSTEM_USER_GROUPS')}</label>
                <p className="whitespaceprewrap">
                  {user.currentSystemGroups
                    ? user.currentSystemGroups.map((g) => g.label).join(', ')
                    : translate('NOT_AVAILABLE')}
                </p>
              </FormGroup>
              <FormGroup
                className="wparag d-flex flex-column gap-4 mt-5"
                style={{ alignItems: 'flex-start', gap: '1rem' }}
              >
                <label>{translate('SYSTEM_USER_ADD_REMOVE_GROUPS')}</label>
              </FormGroup>
              <FormGroup controlId="5e95e18c-a425-4a0d-82c3-f6c32747d50c">
                <InfiniteScrollSelect
                  placeholder={translate('SELECT_GROUPS...')}
                  name="assignTo"
                  asyncLoadFn={async (search: string, page: number) => {
                    const response = await groupsApiService.getOptionsForAsyncPicker(search, page, {
                      type: 'USER_DEFINED'
                    })
                    return response
                  }}
                  onChange={(option: any) => {
                    setFieldValue('assignTo', option)
                  }}
                  customStyles={{
                    placeholder: (base: any) => ({
                      ...base,
                      textAlign: 'left'
                    }),
                    indicatorSeparator: (base: any) => ({
                      ...base,
                      display: 'none'
                    })
                  }}
                  value={values.assignTo}
                  isMulti
                  searchable
                  id="5e95e18c-a425-4a0d-82c3-f6c32747d50c"
                  style={{ width: '561px', maxWidth: '100%', marginBottom: '1rem' }}
                />
              </FormGroup>
              <Form.Group
                controlId="ff5de5b9-ccd1-474c-8a2a-6aef98845ea0"
                className="d-flex flex-column gap-4 mt-2"
                style={{ marginTop: '2rem', alignItems: 'flex-start', gap: '1rem' }}
              >
                <Field
                  name="createRATasksNow"
                  type="checkbox"
                  classesName="px-2"
                  as={Form.Check}
                  label={translate('SYSTEM_USER_CREATE_RA_TASKS')}
                  id="ff5de5b9-ccd1-474c-8a2a-6aef98845ea0"
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button data-action={'cancel'} variant="link" onClick={doClose} id="90df6066-f953-43c1-ad90-18e72ca9931b">
                {translate('CANCEL')}
              </Button>
              <Button
                data-action={'save'}
                className="btn btn-primary text-capitalize"
                onClick={() => handleSubmit()}
                id="3c8c2e21-00e8-4620-b459-a2513dcf500d"
              >
                {translate('OK')}
              </Button>
            </Modal.Footer>
          </Loader>
        )}
      </Formik>
    </Modal>
  )
}

export default ManageUserGroupsModal
